/**
 * Audio input enumeration.
 *
 * Browsers withhold device labels until the user has granted microphone
 * access at least once, so the list is refreshed after `getUserMedia`
 * succeeds. Devices without a label get a stable positional fallback.
 */

import { AudioEngineError, messageFor } from './errors'

/** A selectable audio input. */
export interface AudioInputDevice {
  readonly deviceId: string
  /** Label reported by the browser, or a fallback when it withheld one. */
  readonly label: string
  /** Devices sharing a groupId belong to the same physical hardware. */
  readonly groupId: string
  /** False while the browser withholds the real name (before permission). */
  readonly hasLabel: boolean
}

/**
 * True when the browser is still hiding device names.
 *
 * Until microphone access has been granted once, both Chrome and Safari return
 * a single anonymous entry with an empty id and label, so there is nothing
 * meaningful to choose from yet.
 */
export function areDevicesAnonymous(devices: readonly AudioInputDevice[]): boolean {
  return devices.length > 0 && devices.every((device) => !device.hasLabel)
}

/** localStorage key holding the preferred input. */
export const DEVICE_STORAGE_KEY = 'multitimbral.inputDeviceId'

/** True when the page may access the microphone at all. */
export function isSecureContextAvailable(): boolean {
  return globalThis.isSecureContext
}

/** True when the browser exposes the Media Devices API. */
export function isMediaDevicesAvailable(): boolean {
  if (typeof navigator === 'undefined') return false
  const mediaDevices = navigator.mediaDevices as MediaDevices | undefined
  return typeof mediaDevices?.getUserMedia === 'function'
}

/**
 * Throw the matching {@link AudioEngineError} when the environment cannot
 * support microphone capture at all.
 */
export function assertCaptureSupported(): void {
  if (!isSecureContextAvailable()) {
    throw new AudioEngineError('insecure-context', messageFor('insecure-context'))
  }
  if (!isMediaDevicesAvailable()) {
    throw new AudioEngineError('unsupported', messageFor('unsupported'))
  }
}

/**
 * List the available audio inputs.
 *
 * @returns Inputs in browser order; empty when none are connected.
 */
export async function listAudioInputs(): Promise<readonly AudioInputDevice[]> {
  if (!isMediaDevicesAvailable() || typeof navigator.mediaDevices.enumerateDevices !== 'function') {
    return []
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  const inputs = devices.filter((device) => device.kind === 'audioinput')
  const single = inputs.length === 1

  return inputs.map((device, index) => {
    const hasLabel = device.label !== ''
    if (hasLabel) {
      return { deviceId: device.deviceId, label: device.label, groupId: device.groupId, hasLabel }
    }
    // Anonymous placeholder: say why rather than showing a meaningless number.
    return {
      deviceId: device.deviceId,
      label: single
        ? 'Standardeingang — Mikrofon erlauben, um Namen zu sehen'
        : `Audioeingang ${String(index + 1)}`,
      groupId: device.groupId,
      hasLabel,
    }
  })
}

/**
 * Pick the device to open: the stored preference when it still exists,
 * otherwise the first available input.
 *
 * @returns A deviceId, or null when no input is connected.
 */
export function resolveDeviceId(
  devices: readonly AudioInputDevice[],
  preferredId: string | null,
): string | null {
  if (devices.length === 0) return null
  if (preferredId !== null && devices.some((device) => device.deviceId === preferredId)) {
    return preferredId
  }
  const firstId = devices[0]?.deviceId
  return firstId === undefined || firstId === '' ? null : firstId
}

/**
 * Constraints for a tuner.
 *
 * Echo cancellation, noise suppression and auto gain all reshape the signal —
 * AGC alone can pull a sustained note off pitch — so every one of them stays
 * off. `exact` on the deviceId makes a stale selection fail loudly as
 * `OverconstrainedError` instead of silently opening the wrong input.
 *
 * An empty deviceId is treated as "no preference": Safari reports empty ids
 * for the default input before permission is granted, and pinning `exact: ''`
 * would fail for a device that is in fact available.
 */
export function constraintsFor(deviceId: string | null): MediaStreamConstraints {
  const audio: MediaTrackConstraints = {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
  }
  const hasSelection = deviceId !== null && deviceId !== ''
  return {
    audio: hasSelection ? { ...audio, deviceId: { exact: deviceId } } : audio,
    video: false,
  }
}
